import { InvitationNextStep } from '../api';

export type InvitationStateValue =
    | 'idle'
    | 'loading'
    | 'signup'
    | 'logout_and_signup'
    | 'login'
    | 'accept'
    | 'switch_account'
    | 'completed'
    | 'error';

export type InvitationEvent =
    | { type: 'LOAD' }
    | {
          type: 'RESOLVE';
          payload: { next_step: string | undefined; email?: string; message?: string | null };
      }
    | { type: 'FAIL'; error?: unknown }
    | { type: 'COMPLETE' };

export interface InvitationContext {
    verifyToken?: string;
    error?: unknown;
    email?: string;
    message?: string | null;
}

export interface InvitationState {
    value: InvitationStateValue;
    context: InvitationContext;
}

export const initialInvitationState: InvitationState = {
    value: 'idle',
    context: {},
};

const mapNextStepToState = (nextStep?: string | null): InvitationStateValue => {
    const normalized = typeof nextStep === 'string' ? nextStep.toLowerCase() : undefined;

    switch (normalized) {
        case InvitationNextStep.SignUp:
            return 'signup';
        case InvitationNextStep.LogoutAndSignUp:
            return 'logout_and_signup';
        case InvitationNextStep.Login:
            return 'login';
        case InvitationNextStep.Accept:
            return 'accept';
        case InvitationNextStep.SwitchAccount:
            return 'switch_account';
        default:
            return 'error';
    }
};

export function invitationReducer(state: InvitationState, event: InvitationEvent): InvitationState {
    switch (state.value) {
        case 'idle':
            if (event.type === 'LOAD') {
                return {
                    value: 'loading',
                    context: {
                        ...state.context,
                        email: undefined,
                        message: undefined,
                        error: undefined,
                    },
                };
            }
            return state;

        case 'loading':
            if (event.type === 'RESOLVE') {
                const next = mapNextStepToState(event.payload.next_step);
                return {
                    value: next,
                    context: {
                        ...state.context,
                        email: event.payload.email,
                        message: event.payload.message ?? null,
                    },
                };
            }
            if (event.type === 'FAIL') {
                return {
                    value: 'error',
                    context: { ...state.context, error: event.error, message: undefined },
                };
            }
            return state;

        case 'signup':
        case 'logout_and_signup':
        case 'login':
        case 'accept':
        case 'switch_account':
            if (event.type === 'COMPLETE') {
                return { value: 'completed', context: { ...state.context } };
            }
            return state;

        case 'completed':
            return state;

        case 'error':
            if (event.type === 'LOAD') {
                return {
                    value: 'loading',
                    context: {
                        ...state.context,
                        error: undefined,
                        message: undefined,
                    },
                };
            }
            return state;

        default:
            return state;
    }
}
